import React, { useEffect, useRef, useState, useMemo } from "react";
import KpiCard from "components/NewComponents/NewKpiCard";
import Header from "components/Header";
// Removed: import Chart from "chart.js/auto"; - ChartJS import replaces this
import { Line, Bar } from 'react-chartjs-2'; // Consolidated line and bar import
// Note: Ensure L is imported from 'leaflet'
import { MapContainer, GeoJSON, useMap, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';
import Chart from "chart.js/auto";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    // FIX 1: Import required elements for Line/Area chart (StockOutAreaChart)
    PointElement,
    LineElement,
    Filler,
} from 'chart.js';

// --- Consolidated Chart.js Registration (FIXED) ---
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);


// ----------------------------------------------------


// 1. HELPER COMPONENT: MapBoundsUpdater (FIXED DEFINITION)
const MapBoundsUpdater = ({ geoData }) => {
    const map = useMap();

    const bounds = useMemo(() => {
        if (geoData) {
            const geoJsonLayer = L.geoJSON(geoData);
            return geoJsonLayer.getBounds();
        }
        return null;
    }, [geoData]);

    useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds);
        }
    }, [map, bounds]);

    return null;
};

// 2. HELPER FUNCTION: generateRandomMarkers (FIXED DEFINITION)
const generateRandomMarkers = (count, baseLabel) => {
    const markers = [];
    const generationCount = count + 20;
    for (let i = 0; i < generationCount; i++) {
        // Random latitude (15.5 to 22.0)
        const lat = 15.5 + Math.random() * 6.5;
        // Random longitude (72.5 to 80.0)
        const lon = 72.5 + Math.random() * 7.5;

        markers.push({
            id: `${baseLabel}-${i}`,
            position: [lat, lon],
            label: `${baseLabel} Initiative Point ${i + 1}`,
        });
    }
    return markers;
};

// 3. HELPER FUNCTION: isPointInMaharashtra (FIXED DEFINITION)
const isPointInMaharashtra = (lat, lon, geoData) => {
    if (!geoData || !geoData.features || geoData.features.length === 0) return false;

    const point = L.latLng(lat, lon);

    for (const feature of geoData.features) {
        const layer = L.geoJSON(feature);
        if (layer.getBounds().contains(point)) {
            return true;
        }
    }
    return false;
};

// 4. HELPER COMPONENT: MapLegend (FIXED DEFINITION)
const MapLegend = ({ markerColor, activeTab }) => {
    const choroplethData = [
        { color: '#D4EDDA', label: 'Target Achieved (Green Zone)' },
        { color: '#FFEEBA', label: 'Moderate Progress (Yellow Zone)' },
        { color: '#F8D7DA', label: 'Critical Gap (Red Zone)' }
    ];

    const choroplethTitle = activeTab === 'RMNCH+A'
        ? "District Performance (e.g., IMR/MMR Rate)"
        : "District Performance (e.g., TB Notification Rate)";

    const markerTitle = activeTab === 'RMNCH+A'
        ? "Maternal/Child Health Intervention Site"
        : "NCD Screening or Treatment Site";

    return (
        <div className="absolute bottom-4 right-4 bg-white p-3 rounded-lg border shadow-sm z-[1000] w-72">
            <h4 className="text-sm font-bold text-slate-700 mb-2">Map Legend: {activeTab} Focus</h4>

            <div>
                <p className="text-xs font-semibold text-slate-600 mb-1">Initiative Sites (Dots)</p>
                <div className="flex items-center text-xs text-slate-700">
                    <div style={{ backgroundColor: "#5B21B6" }} className="w-3 h-3 mr-2 rounded-full border border-white shadow-sm"></div>
                    <span>{markerTitle}</span>
                </div>
            </div>
        </div>
    );
};

// 5. HELPER COMPONENT: ScreeningProgressBars (FIXED DEFINITION)
const ScreeningProgressBars = ({ data }) => {
    return (
        <div className="flex flex-col  h-full justify-center space-y-4 pt-1 lg:pt-0">
            <h3 className="text-lg font-bold text-slate-700">Screening Coverage Targets</h3>
            {data.map((item) => (
                <div key={item.name} className="space-y-1">
                    <div className="flex justify-between items-center text-sm font-medium text-slate-600">
                        <span>{item.name}</span>
                        <span>{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className={`h-2.5 rounded-full transition-all duration-700 ${item.color}`}
                            style={{ width: `${item.value}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-slate-500">Target: {item.target}%</p>
                </div>
            ))}
        </div>
    );
};

// 6. HELPER COMPONENT: StyledDonutChart (FIXED)
const hrAvailabilityData = [
    { name: "Nurses", value: 4000, required: 4500 },
    { name: "Doctors", value: 2800, required: 3200 },
    { name: "CHOs", value: 900, required: 1200 },
    { name: "ANMs", value: 1800, required: 2100 },
];

const StyledDonutChart = ({ data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext("2d");

        // Extract values
        const labels = data.map(d => d.name);
        const requiredValues = data.map(d => d.required);  // Full bar
        const currentValues = data.map(d => d.value);      // Filled level

        chartInstanceRef.current = new Chart(ctx, {
            type: "bar",
            data: {
                labels,
                datasets: [
                    {
                        label: "Required",
                        data: requiredValues,
                        backgroundColor: "#D1D5DB", // Medium-Light Gray-300 
                        borderRadius: 6,
                        barPercentage: 1,          // FULL WIDTH
                        categoryPercentage: 0.5,   // Controls spacing (lower = tighter)
                        barThickness: 25,          // Same thickness keeps bars aligned
                    },
                    {
                        label: "Available",
                        data: currentValues,
                        backgroundColor: "#4F46E5",
                        borderRadius: 6,
                        barPercentage: 1,
                        categoryPercentage: 0.5,
                        barThickness: 25,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    datalabels: false,
                    legend: { display: false },
                    tooltip: { enabled: true },
                },
                scales: {
                    x: {
                        stacked: false,
                        grid: { display: false },
                    },
                    y: {
                        grid: { display: true },
                        beginAtZero: true,
                    },
                },
            },
        });


        return () => chartInstanceRef.current?.destroy();
    }, [data]);

    return (
        <div className="p-4 bg-white rounded-xl shadow-md border border-slate-200 h-auto">

            <h3 className="text-lg font-bold text-slate-700 mb-2">
                HR Availability by Cadre
            </h3>

            {/* Bar Chart */}
            <div className="relative w-full h-52">
                <canvas ref={chartRef}></canvas>
            </div>

            {/* Legend */}
            <div className="mt-4 flex justify-center gap-6">
                <div className="flex items-center text-xs text-slate-700">
                    <div className="w-3 h-3 mr-2 rounded-sm bg-slate-300" />
                    Required
                </div>

                <div className="flex items-center text-xs text-slate-700">
                    <div className="w-3 h-3 mr-2 rounded-sm bg-indigo-600" />
                    Available
                </div>
            </div>
        </div>
    );
};

// 7. HELPER COMPONENT: StockOutAreaChart (FIXED)
// 2. Mock Data
const stockOutTrendData = [
    { month: 'Jan', days: 5 },
    { month: 'Feb', days: 10 },
    { month: 'Mar', days: 3 },
    { month: 'Apr', days: 5 },
    { month: 'May', days: 9 },
    { month: 'Jun', days: 1 },
    { month: 'Jul', days: 3 },
    { month: 'Aug', days: 5 },
    { month: 'Sept', days: 9 },
    { month: 'Oct', days: 1 },
    { month: 'Nov', days: 9 },
    { month: 'Dec', days: 1 },
];

const ddata = {
    labels: stockOutTrendData.map(d => d.month),
    datasets: [
        {
            label: 'Total Stock-Out Days',
            data: stockOutTrendData.map(d => d.days),
            // Styling for the line
            borderColor: 'rgba(79, 70, 229, 1)', // Indigo
            pointBackgroundColor: 'rgba(79, 70, 229, 1)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgba(79, 70, 229, 1)',
            tension: 0.3, // Curve the line slightly

            // Styling for the Area fill
            backgroundColor: 'rgba(79, 70, 229, 0.2)', // Light Indigo for the fill
            fill: 'start', // Start filling from the bottom (x-axis)
        },
    ],
};

// 3. Chart Options
const optionss = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false, // We only have one dataset

        },
        datalabels: false,
        title: {
            display: true,
            text: 'Monthly Trend of Total Stock-Out Days',
            font: {
                size: 18,
                weight: 'bold',
            },
            color: '#334155', // slate-700
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function (context) {
                    let label = context.dataset.label || '';
                    if (label) {
                        label += ': ';
                    }
                    if (context.parsed.y !== null) {
                        label += new Intl.NumberFormat('en-US').format(context.parsed.y) + ' Days';
                    }
                    return label;
                }
            }
        }
    },
    scales: {
        x: {
            title: {
                display: true,
                text: 'Months (2025)',
                font: { weight: 'bold' },
            },
            grid: {
                display: false,
            }
        },
        y: {
            beginAtZero: true,
            title: {
                display: true,
                text: 'Total Stock-Out Days',
                font: { weight: 'bold' },
            },
            // Ensure only integer values are shown on the Y-axis
            ticks: {
                callback: function (value) {
                    if (value % 1 === 0) {
                        return value;
                    }
                }
            },
            grid: {
                color: 'rgba(203, 213, 225, 0.5)',
            }
        }
    }
};

// 4. React Component
const StockOutAreaChart = () => {
    return (
        <div className="p-4 bg-white rounded-xl shadow-md border border-slate-100 h-full">
            {/* Set a fixed height for a decent-looking chart */}
            <div style={{ height: '265px' }}>
                <Line data={ddata} options={optionss} />
            </div>
        </div>
    );
};

// 8. HELPER COMPONENT: TopDrugsBarChart (FIXED)
// 2. Mock Data
const drugData = [
    { name: 'Paracetamol 500mg', dispensed: 78500 },
    { name: 'Amoxicillin 250mg', dispensed: 62100 },
    { name: 'ORS (Packs)', dispensed: 55900 },
    { name: 'Metformin 500mg', dispensed: 41200 },
    { name: 'Iron Folic Acid', dispensed: 38800 },
];

const barChartData = {
    labels: drugData.map(d => d.name),
    datasets: [
        {
            label: 'Units Dispensed',
            data: drugData.map(d => d.dispensed),
            backgroundColor: [
                'rgba(79, 70, 229, 0.8)', // Indigo (for the top drug)
                'rgba(16, 185, 129, 0.8)', // Emerald
                'rgba(245, 158, 11, 0.8)', // Amber
                'rgba(59, 130, 246, 0.8)', // Blue
                'rgba(236, 72, 153, 0.8)', // Pink
            ],
            borderColor: [
                'rgba(79, 70, 229, 1)',
                'rgba(16, 185, 129, 1)',
                'rgba(245, 158, 11, 1)',
                'rgba(59, 130, 246, 1)',
                'rgba(236, 72, 153, 1)',
            ],
            borderWidth: 1,
            borderRadius: 4, // Adds rounded corners to bars
        },
    ],
};

// 3. Chart Options
const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allows you to control height
    indexAxis: 'y', // Makes it a horizontal bar chart
    plugins: {
        legend: {
            display: false, // We only have one dataset, so hide the legend
        },
        title: {
            display: false, // Title is handled by h3 outside the chart
        },
        datalabels: false
    },
    scales: {
        x: {
            beginAtZero: true,
            title: {
                display: true,
                text: 'Total Units Dispensed',
                font: { weight: 'bold' },
            },
            grid: {
                display: false,
            }
        },
        y: {
            title: {
                display: false,
            },
            grid: {
                // Keep horizontal lines visible but lighter
                color: 'rgba(203, 213, 225, 0.5)', // slate-300
            }
        }
    }
};

// 4. React Component
const TopDrugsBarChart = () => {
    return (
        <div className="px-4 pt-4 pb-2 bg-white rounded-xl shadow-md border border-slate-100 h-full">
            <h3 className="text-lg font-bold text-slate-700">Top 5 Drugs Dispensed</h3>
            {/* Set a fixed height for a decent-looking chart */}
            <div style={{ height: '240px' }}>
                <Bar data={barChartData} options={barChartOptions} />
            </div>
        </div>
    );
};
// ----------------------------------------------------

// --- Styling Constants & Data ---
// const mapBaseStyle = {
//     fillColor: "#f0f0f0",
//     color: "#9e9e9e",
//     weight: 1,
//     opacity: 1,
//     fillOpacity: 1,
// };
const mapBaseStyle = {
    fillColor: "#ffc399",
    color: "#D55209",
    weight: 1,
    opacity: 1,
    fillOpacity: 1,
};
const markerColor = "#5B21B6";

// 🌟 NEW: NCD Screening Data (from user input)
const NCD_SCREENING_DATA = [
    { name: "Oral Cancer Screening", value: 72, target: 80, color: "bg-emerald-500" },
    { name: "Breast Cancer Screening", value: 45, target: 80, color: "bg-yellow-500" },
    { name: "Cervical Cancer Screening", value: 58, target: 80, color: "bg-cyan-500" },
    { name: "Hypertension Screening", value: 85, target: 90, color: "bg-blue-500" },
    { name: "Diabetes Screening", value: 65, target: 90, color: "bg-fuchsia-500" },
];

// 🌟 NEW: RMNCHA Screening Data (relevant mock data)
const RMNCHA_SCREENING_DATA = [
    { name: "Full Antenatal Care (ANC)", value: 82, target: 90, color: "bg-indigo-500" },
    { name: "Institutional Deliveries", value: 94, target: 95, color: "bg-green-500" },
    { name: "Immunization (DPT-3) Coverage", value: 78, target: 85, color: "bg-red-500" },
    { name: "Early Postnatal Care (PNC)", value: 75, target: 80, color: "bg-orange-500" },
    { name: "Adolescent Health Checkups", value: 60, target: 75, color: "bg-purple-500" },
];

const RMNCHA_MARKERS = [
    { id: 1, district: "Pune", position: [18.5204, 73.8567], label: "RMNCH+A Site", data: { fullANC: 1234, institutionalDeliveries: 1120, immunizationDPT3: 980, earlyPNC: 870, adolescentHealth: 450 } },
    { id: 2, district: "Mumbai", position: [19.0760, 72.8777], label: "RMNCH+A Site", data: { fullANC: 2340, institutionalDeliveries: 2100, immunizationDPT3: 1980, earlyPNC: 1870, adolescentHealth: 980 } },
    { id: 3, district: "Nagpur", position: [21.1458, 79.0882], label: "RMNCH+A Site", data: { fullANC: 980, institutionalDeliveries: 870, immunizationDPT3: 780, earlyPNC: 670, adolescentHealth: 450 } },
    { id: 4, district: "Aurangabad", position: [19.8762, 75.3433], label: "RMNCH+A Site", data: { fullANC: 1120, institutionalDeliveries: 980, immunizationDPT3: 890, earlyPNC: 780, adolescentHealth: 500 } },
    { id: 5, district: "Kolhapur", position: [16.7050, 74.2433], label: "RMNCH+A Site", data: { fullANC: 890, institutionalDeliveries: 810, immunizationDPT3: 740, earlyPNC: 680, adolescentHealth: 420 } },
    { id: 6, district: "Solapur", position: [17.6599, 75.9082], label: "RMNCH+A Site", data: { fullANC: 950, institutionalDeliveries: 870, immunizationDPT3: 800, earlyPNC: 720, adolescentHealth: 460 } },
    { id: 7, district: "Amravati", position: [20.9333, 77.7500], label: "RMNCH+A Site", data: { fullANC: 1100, institutionalDeliveries: 1020, immunizationDPT3: 940, earlyPNC: 880, adolescentHealth: 490 } },
    { id: 8, district: "Latur", position: [18.4069, 76.5619], label: "RMNCH+A Site", data: { fullANC: 780, institutionalDeliveries: 710, immunizationDPT3: 650, earlyPNC: 600, adolescentHealth: 380 } },
    { id: 9, district: "Raigad", position: [18.6435, 73.2351], label: "RMNCH+A Site", data: { fullANC: 1020, institutionalDeliveries: 950, immunizationDPT3: 890, earlyPNC: 830, adolescentHealth: 470 } },
    { id: 10, district: "Nashik", position: [20.0000, 73.7800], label: "RMNCH+A Site", data: { fullANC: 1250, institutionalDeliveries: 1150, immunizationDPT3: 1040, earlyPNC: 950, adolescentHealth: 520 } },
    { id: 11, district: "Satara", position: [17.6848, 74.0083], label: "RMNCH+A Site", data: { fullANC: 890, institutionalDeliveries: 820, immunizationDPT3: 760, earlyPNC: 700, adolescentHealth: 420 } },
    { id: 12, district: "Jalgaon", position: [21.0000, 75.5667], label: "RMNCH+A Site", data: { fullANC: 980, institutionalDeliveries: 900, immunizationDPT3: 830, earlyPNC: 760, adolescentHealth: 450 } },
    { id: 13, district: "Wardha", position: [20.7397, 78.6019], label: "RMNCH+A Site", data: { fullANC: 1050, institutionalDeliveries: 970, immunizationDPT3: 890, earlyPNC: 820, adolescentHealth: 480 } },
    { id: 14, district: "Thane", position: [19.2183, 73.1082], label: "RMNCH+A Site", data: { fullANC: 1400, institutionalDeliveries: 1300, immunizationDPT3: 1200, earlyPNC: 1100, adolescentHealth: 600 } },
    { id: 15, district: "Palghar", position: [19.6800, 72.7700], label: "RMNCH+A Site", data: { fullANC: 870, institutionalDeliveries: 800, immunizationDPT3: 740, earlyPNC: 680, adolescentHealth: 420 } },
    { id: 16, district: "Gadchiroli", position: [20.1800, 80.0000], label: "RMNCH+A Site", data: { fullANC: 670, institutionalDeliveries: 610, immunizationDPT3: 560, earlyPNC: 500, adolescentHealth: 320 } },
    { id: 17, district: "Bhandara", position: [21.1764, 79.9194], label: "RMNCH+A Site", data: { fullANC: 780, institutionalDeliveries: 710, immunizationDPT3: 650, earlyPNC: 600, adolescentHealth: 380 } },
    { id: 18, district: "Chandrapur", position: [19.9691, 79.3000], label: "RMNCH+A Site", data: { fullANC: 900, institutionalDeliveries: 830, immunizationDPT3: 760, earlyPNC: 700, adolescentHealth: 420 } },
    { id: 19, district: "Yavatmal", position: [20.3900, 78.1300], label: "RMNCH+A Site", data: { fullANC: 800, institutionalDeliveries: 740, immunizationDPT3: 680, earlyPNC: 620, adolescentHealth: 380 } },
    { id: 20, district: "Nanded", position: [19.1436, 77.3000], label: "RMNCH+A Site", data: { fullANC: 870, institutionalDeliveries: 810, immunizationDPT3: 750, earlyPNC: 700, adolescentHealth: 420 } },
    { id: 21, district: "Parbhani", position: [19.2600, 76.7700], label: "RMNCH+A Site", data: { fullANC: 780, institutionalDeliveries: 720, immunizationDPT3: 660, earlyPNC: 600, adolescentHealth: 380 } },
    { id: 22, district: "Hingoli", position: [19.7200, 77.1500], label: "RMNCH+A Site", data: { fullANC: 650, institutionalDeliveries: 590, immunizationDPT3: 540, earlyPNC: 480, adolescentHealth: 320 } },
    { id: 23, district: "Ahmednagar", position: [19.0967, 74.7478], label: "RMNCH+A Site", data: { fullANC: 1100, institutionalDeliveries: 1020, immunizationDPT3: 950, earlyPNC: 880, adolescentHealth: 480 } },
    { id: 24, district: "Beed", position: [18.9868, 75.7600], label: "RMNCH+A Site", data: { fullANC: 800, institutionalDeliveries: 740, immunizationDPT3: 680, earlyPNC: 620, adolescentHealth: 380 } },
    { id: 25, district: "Osmanabad", position: [18.1900, 76.1300], label: "RMNCH+A Site", data: { fullANC: 700, institutionalDeliveries: 640, immunizationDPT3: 580, earlyPNC: 520, adolescentHealth: 340 } },
    { id: 26, district: "Ratnagiri", position: [16.9900, 73.3000], label: "RMNCH+A Site", data: { fullANC: 780, institutionalDeliveries: 710, immunizationDPT3: 650, earlyPNC: 600, adolescentHealth: 380 } },
    { id: 27, district: "Sindhudurg", position: [16.0333, 73.7667], label: "RMNCH+A Site", data: { fullANC: 650, institutionalDeliveries: 590, immunizationDPT3: 540, earlyPNC: 480, adolescentHealth: 320 } },
    { id: 28, district: "Dhule", position: [20.9000, 74.7700], label: "RMNCH+A Site", data: { fullANC: 900, institutionalDeliveries: 830, immunizationDPT3: 760, earlyPNC: 700, adolescentHealth: 420 } },
    { id: 29, district: "Jalna", position: [19.8300, 75.9000], label: "RMNCH+A Site", data: { fullANC: 780, institutionalDeliveries: 720, immunizationDPT3: 660, earlyPNC: 600, adolescentHealth: 380 } },
    { id: 30, district: "Akola", position: [20.7000, 77.0000], label: "RMNCH+A Site", data: { fullANC: 850, institutionalDeliveries: 790, immunizationDPT3: 730, earlyPNC: 670, adolescentHealth: 400 } },
    { id: 31, district: "Buldhana", position: [20.5167, 76.1833], label: "RMNCH+A Site", data: { fullANC: 800, institutionalDeliveries: 740, immunizationDPT3: 680, earlyPNC: 620, adolescentHealth: 380 } },
    { id: 32, district: "Gondia", position: [21.4650, 80.2300], label: "RMNCH+A Site", data: { fullANC: 670, institutionalDeliveries: 610, immunizationDPT3: 560, earlyPNC: 500, adolescentHealth: 320 } },
    { id: 33, district: "Nandurbar", position: [21.3600, 74.2500], label: "RMNCH+A Site", data: { fullANC: 750, institutionalDeliveries: 690, immunizationDPT3: 630, earlyPNC: 570, adolescentHealth: 350 } },
    { id: 34, district: "Washim", position: [20.1000, 77.1500], label: "RMNCH+A Site", data: { fullANC: 700, institutionalDeliveries: 640, immunizationDPT3: 580, earlyPNC: 520, adolescentHealth: 340 } },
    { id: 200, district: "Sangli", position: [16.8524, 74.5815], label: "RMNCH+A Site (Placeholder)", data: { fullANC: 700, institutionalDeliveries: 650, immunizationDPT3: 600, earlyPNC: 550, adolescentHealth: 300 } },
    { id: 201, district: "Mumbai Suburban", position: [19.1670, 72.8570], label: "RMNCH+A Site (Placeholder)", data: { fullANC: 700, institutionalDeliveries: 650, immunizationDPT3: 600, earlyPNC: 550, adolescentHealth: 300 } }
];

const NCD_MARKERS = [
    { id: 101, district: "Pune", position: [18.5204, 73.8567], label: "NCD Screening Site", data: { oralCancer: 850, breastCancer: 790, cervicalCancer: 730, hypertension: 670, diabetes: 400 } },
    { id: 102, district: "Mumbai", position: [19.0760, 72.8777], label: "NCD Screening Site", data: { oralCancer: 920, breastCancer: 880, cervicalCancer: 810, hypertension: 700, diabetes: 450 } },
    { id: 103, district: "Nagpur", position: [21.1458, 79.0882], label: "NCD Screening Site", data: { oralCancer: 780, breastCancer: 740, cervicalCancer: 700, hypertension: 650, diabetes: 380 } },
    { id: 104, district: "Aurangabad", position: [19.8762, 75.3433], label: "NCD Screening Site", data: { oralCancer: 820, breastCancer: 770, cervicalCancer: 720, hypertension: 680, diabetes: 400 } },
    { id: 105, district: "Kolhapur", position: [16.7050, 74.2433], label: "NCD Screening Site", data: { oralCancer: 750, breastCancer: 700, cervicalCancer: 650, hypertension: 600, diabetes: 350 } },
    { id: 106, district: "Solapur", position: [17.6599, 75.9082], label: "NCD Screening Site", data: { oralCancer: 710, breastCancer: 680, cervicalCancer: 630, hypertension: 590, diabetes: 320 } },
    { id: 107, district: "Amravati", position: [20.9333, 77.7500], label: "NCD Screening Site", data: { oralCancer: 790, breastCancer: 740, cervicalCancer: 710, hypertension: 660, diabetes: 370 } },
    { id: 108, district: "Latur", position: [18.4069, 76.5619], label: "NCD Screening Site", data: { oralCancer: 680, breastCancer: 650, cervicalCancer: 600, hypertension: 570, diabetes: 300 } },
    { id: 109, district: "Raigad", position: [18.6435, 73.2351], label: "NCD Screening Site", data: { oralCancer: 720, breastCancer: 680, cervicalCancer: 640, hypertension: 600, diabetes: 330 } },
    { id: 110, district: "Nashik", position: [20.0000, 73.7800], label: "NCD Screening Site", data: { oralCancer: 810, breastCancer: 770, cervicalCancer: 730, hypertension: 690, diabetes: 400 } },
    { id: 111, district: "Satara", position: [17.6848, 74.0083], label: "NCD Screening Site", data: { oralCancer: 700, breastCancer: 660, cervicalCancer: 620, hypertension: 580, diabetes: 320 } },
    { id: 112, district: "Jalgaon", position: [21.0000, 75.5667], label: "NCD Screening Site", data: { oralCancer: 760, breastCancer: 720, cervicalCancer: 680, hypertension: 640, diabetes: 350 } },
    { id: 113, district: "Wardha", position: [20.7397, 78.6019], label: "NCD Screening Site", data: { oralCancer: 740, breastCancer: 700, cervicalCancer: 660, hypertension: 620, diabetes: 340 } },
    { id: 114, district: "Parbhani", position: [19.2600, 76.7700], label: "NCD Screening Site", data: { oralCancer: 690, breastCancer: 650, cervicalCancer: 610, hypertension: 580, diabetes: 310 } },
    { id: 115, district: "Osmanabad", position: [18.1900, 76.1300], label: "NCD Screening Site", data: { oralCancer: 670, breastCancer: 630, cervicalCancer: 590, hypertension: 550, diabetes: 300 } },
    { id: 116, district: "Chandrapur", position: [19.9691, 79.3000], label: "NCD Screening Site", data: { oralCancer: 710, breastCancer: 680, cervicalCancer: 640, hypertension: 600, diabetes: 320 } },
    { id: 117, district: "Thane", position: [19.2183, 73.1082], label: "NCD Screening Site", data: { oralCancer: 880, breastCancer: 840, cervicalCancer: 800, hypertension: 760, diabetes: 450 } },
    { id: 118, district: "Palghar", position: [19.6800, 72.7700], label: "NCD Screening Site", data: { oralCancer: 720, breastCancer: 680, cervicalCancer: 640, hypertension: 600, diabetes: 350 } },
    { id: 119, district: "Gadchiroli", position: [20.1800, 80.0000], label: "NCD Screening Site", data: { oralCancer: 650, breastCancer: 620, cervicalCancer: 580, hypertension: 550, diabetes: 280 } },
    { id: 120, district: "Bhandara", position: [21.1764, 79.9194], label: "NCD Screening Site", data: { oralCancer: 680, breastCancer: 640, cervicalCancer: 600, hypertension: 570, diabetes: 300 } },
    { id: 121, district: "Yavatmal", position: [20.3900, 78.1300], label: "NCD Screening Site", data: { oralCancer: 700, breastCancer: 660, cervicalCancer: 620, hypertension: 580, diabetes: 320 } },
    { id: 122, district: "Nanded", position: [19.1436, 77.3000], label: "NCD Screening Site", data: { oralCancer: 720, breastCancer: 680, cervicalCancer: 640, hypertension: 600, diabetes: 340 } },
    { id: 123, district: "Hingoli", position: [19.7200, 77.1500], label: "NCD Screening Site", data: { oralCancer: 660, breastCancer: 620, cervicalCancer: 580, hypertension: 550, diabetes: 300 } },
    { id: 124, district: "Ahmednagar", position: [19.0967, 74.7478], label: "NCD Screening Site", data: { oralCancer: 780, breastCancer: 740, cervicalCancer: 700, hypertension: 660, diabetes: 380 } },
    { id: 125, district: "Beed", position: [18.9868, 75.7600], label: "NCD Screening Site", data: { oralCancer: 710, breastCancer: 670, cervicalCancer: 630, hypertension: 590, diabetes: 330 } },
    { id: 126, district: "Ratnagiri", position: [16.9900, 73.3000], label: "NCD Screening Site", data: { oralCancer: 650, breastCancer: 620, cervicalCancer: 580, hypertension: 550, diabetes: 300 } },
    { id: 127, district: "Sindhudurg", position: [16.0333, 73.7667], label: "NCD Screening Site", data: { oralCancer: 620, breastCancer: 590, cervicalCancer: 550, hypertension: 520, diabetes: 280 } },
    { id: 128, district: "Dhule", position: [20.9000, 74.7700], label: "NCD Screening Site", data: { oralCancer: 710, breastCancer: 680, cervicalCancer: 640, hypertension: 600, diabetes: 320 } },
    { id: 129, district: "Jalna", position: [19.8300, 75.9000], label: "NCD Screening Site", data: { oralCancer: 680, breastCancer: 650, cervicalCancer: 610, hypertension: 580, diabetes: 300 } },
    { id: 130, district: "Akola", position: [20.7000, 77.0000], label: "NCD Screening Site", data: { oralCancer: 700, breastCancer: 660, cervicalCancer: 620, hypertension: 580, diabetes: 320 } },
    { id: 131, district: "Buldhana", position: [20.5167, 76.1833], label: "NCD Screening Site", data: { oralCancer: 680, breastCancer: 640, cervicalCancer: 600, hypertension: 570, diabetes: 310 } },
    { id: 132, district: "Gondia", position: [21.4650, 80.2300], label: "NCD Screening Site", data: { oralCancer: 650, breastCancer: 620, cervicalCancer: 580, hypertension: 550, diabetes: 290 } },
    { id: 133, district: "Nandurbar", position: [21.3600, 74.2500], label: "NCD Screening Site", data: { oralCancer: 670, breastCancer: 640, cervicalCancer: 600, hypertension: 570, diabetes: 310 } },
    { id: 134, district: "Washim", position: [20.1000, 77.1500], label: "NCD Screening Site", data: { oralCancer: 660, breastCancer: 620, cervicalCancer: 580, hypertension: 550, diabetes: 300 } },
    { id: 200, district: "Sangli", position: [16.8524, 74.5815], label: "NCD Screening Site (Placeholder)", data: { oralCancer: 600, breastCancer: 550, cervicalCancer: 500, hypertension: 700, diabetes: 400 } },
    { id: 201, district: "Mumbai Suburban", position: [19.1670, 72.8570], label: "NCD Screening Site (Placeholder)", data: { oralCancer: 600, breastCancer: 550, cervicalCancer: 500, hypertension: 700, diabetes: 400 } }
];
const ANEMIA_SEVERITY_DATA = {
    labels: [
        "Pune", "Mumbai", "Nagpur", "Nashik", "Aurangabad",
        "Amravati", "Solapur", "Kolhapur"
    ],
    datasets: [
        {
            label: "Mild Anemia",
            data: [22, 25, 28, 26, 30, 27, 24, 23],
            backgroundColor: "#40C79A", // soft blue
            borderRadius: 3,
            borderSkipped: false,
        },
        {
            label: "Moderate Anemia",
            data: [14, 16, 18, 17, 19, 18, 15, 14],
            backgroundColor: "#629BF8", // soft orange
            borderRadius: 3,
            borderSkipped: false,
        },
        {
            label: "Severe Anemia",
            data: [3, 4, 5, 4, 6, 5, 4, 3],
            backgroundColor: "#4F46E5", // deep red (not harsh)
            borderRadius: 3,
            borderSkipped: false,
        },
    ],

};
const anemiaChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "top",
            labels: {
                boxWidth: 14,
                font: { size: 12 }
            }
        },
        title: {
            display: true,
            text: "District-wise Anemia Severity Distribution (%)",
            font: {
                size: 16,
                weight: "bold"
            },
            color: "#334155",
        },
        tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`
            }
        },
        datalabels: false
    },
    scales: {
        x: {
            stacked: true,
            grid: { display: false }
        },
        y: {
            stacked: true,
            beginAtZero: true,
            title: {
                display: true,
                text: "Percentage (%)",
                font: { weight: "bold" }
            }
        }
    }
};
const AnemiaSeverityChart = () => {
    return (
        <div className="p-4 bg-white rounded-xl shadow-md border border-slate-100 h-full">
            <div style={{ height: "300px" }}>
                <Bar
                    data={ANEMIA_SEVERITY_DATA}
                    options={anemiaChartOptions}
                />
            </div>
        </div>
    );
};

// ====================================================
// MAIN COMPONENT
// ====================================================
const IntegratedHealthDashboard = () => {
    const handleMenuClick = () => { /* Sidebar click */ };
    const [activeTab, setActiveTab] = useState("RMNCH+A");
    const [geoData, setGeoData] = useState(null);

    const initialMarkerPool = useMemo(() => ({
        // FIX: generateRandomMarkers is now defined above
        "RMNCH+A": generateRandomMarkers(50, "RMNCH+A Site"),
        "NCD": generateRandomMarkers(50, "NCD Screening Site"),
    }), []);

    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/geojson/1.geojson")
            .then((res) => res.json())
            .then((data) => setGeoData(data))
            .catch((err) => console.error("GeoJSON fetch error:", err));
    }, []);

    const finalMarkers = useMemo(() => {
        if (!geoData) return { "RMNCH+A": [], "NCD": [] };

        return {
            "RMNCH+A": RMNCHA_MARKERS,
            "NCD": NCD_MARKERS,
        };
    }, [geoData]); // geoData dependency remains to ensure map is loaded

    const DateRangeLine = () => {
        const today = new Date();
        // 1st day of current month
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

        // Format function
        const formatDate = (date) =>
            date.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

        return (
            <span className="text-sm text-slate-500 font-medium truncate px-3 py-1">State : <span className='fw-bold text-slate-700 pr-3'>Maharashtra</span> Date Range: <span className='fw-bold text-slate-700'>{formatDate(firstDay)} to {formatDate(today)}</span></span>
        );
    };
    // 🌟 NEW: Select data based on active tab
    const dataToDisplay = activeTab === 'NCD' ? NCD_SCREENING_DATA : RMNCHA_SCREENING_DATA;


    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                onMenuClick={handleMenuClick}
                dashboardName="Integrated Health Dashboard"
            />

            <div className="w-full p-2 bg-gray-50">
                {/* KPI Cards (existing code here) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-2 mb-2">
                    <KpiCard
                        title="Total Maternal Deaths"
                        value="33"
                        target="20"
                        unit="deaths"
                        trend={[29, 40, 28, 37, 30, 42, 27, 33]}
                    />

                    <KpiCard
                        title="Total Child Deaths"
                        value="88"
                        // target="70"
                        unit="deaths"
                        trend={[78, 95, 82, 91, 90, 97, 90, 89]}
                    />

                    <KpiCard
                        title="Hypertension Screening"
                        value="1.2 Cr"
                        target="1.5 Cr"
                        unit="tests"
                        trend={[900000, 1300000, 1100000, 1500000, 1000000, 1250000, 1200000]}
                    />

                    <KpiCard
                        title="Diabetes Screening"
                        value="900K"
                        target="1 Cr"
                        unit="tests"
                        trend={[780000, 920000, 850000, 930000, 860000, 910000, 900000]}
                    />

                    <KpiCard
                        title="TB Notification Rate"
                        value="78%"
                        target="85%"
                        unit=""
                        trend={[55, 80, 70, 65, 82, 60, 75, 78]}
                    />
                    <KpiCard title="Treatment Initiation Rate (TIR)" value="91%" target="95%" unit="" trend={[95, 85, 90, 80, 91, 92]} />
                    <KpiCard
                        title="Anemia Prevalence (Women 15–49)"
                        value="38%"
                        target="≤30%"
                        unit=""
                        trend={[45, 43, 41, 40, 39, 38]}
                    />

                </div>

                {/* -------------------- Map and Side Panel Layout -------------------- */}
                {/* 🔹 ROW: Map + Side Panel (col-6) AND Charts (col-6) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">

                    {/* ⭐ LEFT SIDE — MAP & PROGRESS BARS (6 Columns) */}
                    <div className="bg-white lg:col-span-5 rounded-xl shadow-md border border-slate-100 p-4 h-[640px]">
                        {/* Tabs UI */}
                        <div className="flex border-b border-gray-200 mb-2">
                            {["RMNCH+A", "NCD"].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 text-sm font-medium transition-colors duration-150 ${activeTab === tab
                                        ? "text-indigo-600 border-b-2 border-indigo-600"
                                        : "text-gray-500 hover:text-gray-700"
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Map + Progress Bars */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">

                            {/* MAP */}
                            <div className="lg:col-span-8 h-[480px] w-full relative">
                                {geoData && (
                                    <MapContainer
                                        center={[19.5, 76.5]}
                                        zoom={6}
                                        minZoom={6}
                                        maxZoom={7}

                                        // ⭐ More expanded bounds
                                        maxBounds={[[12, 68], [26, 84]]}
                                        maxBoundsViscosity={0.5}

                                        style={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
                                        scrollWheelZoom={false}
                                        doubleClickZoom={false}
                                        zoomControl={false}
                                        boxZoom={false}
                                        keyboard={false}
                                        touchZoom={false}
                                    >

                                        <GeoJSON data={geoData} style={mapBaseStyle} />
                                        <MapBoundsUpdater geoData={geoData} />

                                        {finalMarkers[activeTab].map((marker) => (
                                            <CircleMarker
                                                key={marker.id}
                                                center={marker.position}
                                                radius={5}
                                                pathOptions={{
                                                    fillColor: activeTab === 0 ? markerColor : markerColor, // Example: orange for RMNCHA, green for NCD
                                                    color: "#fff",
                                                    weight: 1.5,
                                                    opacity: 1,
                                                    fillOpacity: 0.9,


                                                }}
                                            >
                                                <Popup>
                                                    <span className="font-semibold text-slate-800">District: {marker.district}</span>
                                                    <br />

                                                    {/* RMNCHA Data */}
                                                    {marker.data.fullANC !== undefined && (
                                                        <>
                                                            <span className="text-sm text-slate-600">Full Antenatal Care (ANC): {marker.data.fullANC}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Institutional Deliveries: {marker.data.institutionalDeliveries}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Immunization (DPT-3) Coverage: {marker.data.immunizationDPT3}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Early Postnatal Care (PNC): {marker.data.earlyPNC}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Adolescent Health Checkups: {marker.data.adolescentHealth}</span>
                                                        </>
                                                    )}

                                                    {/* NCD Data */}
                                                    {marker.data.oralCancer !== undefined && (
                                                        <>
                                                            <span className="text-sm text-slate-600">Oral Cancer: {marker.data.oralCancer}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Breast Cancer: {marker.data.breastCancer}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Cervical Cancer: {marker.data.cervicalCancer}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Hypertension: {marker.data.hypertension}</span>
                                                            <br />
                                                            <span className="text-sm text-slate-600">Diabetes: {marker.data.diabetes}</span>
                                                        </>
                                                    )}
                                                </Popup>

                                            </CircleMarker>
                                        ))}


                                        <MapLegend markerColor={markerColor} activeTab={activeTab} />
                                    </MapContainer>
                                )}
                            </div>

                            {/* SIDE PANEL - NOW USES dataToDisplay */}
                            <div className="lg:col-span-4 justify-content-center align-items-center">
                                <ScreeningProgressBars data={dataToDisplay} />
                            </div>

                        </div>
                    </div>

                    {/* ⭐ RIGHT SIDE — CHARTS (6 Columns) */}
                    <div className="lg:col-span-7 grid grid-cols-1 lg:grid-cols-2 gap-2">

                        {/* Row 1: Donut Chart & Bar Chart */}
                        <div className="lg:col-span-1">
                            <StyledDonutChart data={hrAvailabilityData} />
                        </div>

                        <div className="lg:col-span-1">
                            <TopDrugsBarChart />
                        </div>
                        <div className="lg:col-span-1">
                            <AnemiaSeverityChart />
                        </div>
                        <div className="lg:col-span-1">
                            <StockOutAreaChart />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IntegratedHealthDashboard;